import { useEffect, useState } from 'react'
import type { DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { useReorderFacts } from '@/application/services/fact.service'
import type { Fact } from '@/domains/types'

interface UseFactReorderOptions {
  leadId: string
  /** Full, already-sorted (server order) and filtered fact list. */
  facts: Fact[]
  onSuccess?: () => void
  onError?: (message: string) => void
}

interface UseFactReorderResult {
  orderedFacts: Fact[]
  handleDragEnd: (event: DragEndEvent) => Promise<void>
}

/**
 * Owns the optimistic reordering of a lead's facts.
 *
 * The admin fact list is never paginated, so a drag reorders the whole
 * list — which is also what the backend requires: the complete, exact set
 * of the lead's fact ids on every reorder call. This hook keeps a local
 * copy of that order so the move shows up immediately, and sends the
 * resulting full id list to the server.
 *
 * On success the new order sticks (until the next server refresh); on
 * failure the local order is rolled back to what it was before the drag.
 */
export function useFactReorder({
  leadId,
  facts,
  onSuccess,
  onError,
}: UseFactReorderOptions): UseFactReorderResult {
  const [localFacts, setLocalFacts] = useState<Fact[]>(facts)
  const { mutateAsync: reorderFacts } = useReorderFacts(leadId)

  useEffect(() => {
    setLocalFacts(facts)
  }, [facts])

  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    const { active, over } = event
    if (!over) return
    const activeId = String(active.id)
    const overId = String(over.id)
    if (activeId === overId) return

    const fromIndex = localFacts.findIndex((fact) => fact.id === activeId)
    const toIndex = localFacts.findIndex((fact) => fact.id === overId)
    if (fromIndex === -1 || toIndex === -1) return

    const previous = localFacts
    const next = arrayMove(localFacts, fromIndex, toIndex)
    setLocalFacts(next)

    try {
      await reorderFacts(next.map((fact) => fact.id))
      onSuccess?.()
    } catch (error: unknown) {
      setLocalFacts(previous)
      onError?.(error instanceof Error ? error.message : String(error))
    }
  }

  return { orderedFacts: localFacts, handleDragEnd }
}
